/**
 * StructuredRoleSheetDialog — dialog nộp Bảng phân vai dạng structured (4 vai)
 * dùng chung cho:
 *  - BracketsPage (admin/BTC): nộp thay nhiều đội, chọn round + team trong dialog.
 *  - RoleSheetsPage (student): nộp cho đội của mình ở vòng Bán kết / Chung kết.
 *
 * Tách khỏi BracketsPage 2026-05-25 để R2 student có chỗ nộp structured 4 vai
 * (trước đây chỉ có upload PDF). Backend hỗ trợ via POST
 * /role-assignment-sheets/structured — auto-compute late penalty T-48h.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { AssignmentInd, CheckCircle, Schedule, Warning } from '@mui/icons-material'

import {
  useSubmitStructuredRoleSheetMutation,
  useGetTeamRoundSheetQuery,
  type RoleEntry,
  type RolePosition,
} from '../store/api/bracketsApi'

export const ROLE_LABELS: Record<RolePosition, string> = {
  party: 'Đương sự',
  lawyer_lead: 'Người bảo vệ chính (Trưởng nhóm)',
  lawyer_support_1: 'Người bảo vệ hỗ trợ 1',
  lawyer_support_2: 'Người bảo vệ hỗ trợ 2',
}

const ROLE_ORDER: RolePosition[] = ['party', 'lawyer_lead', 'lawyer_support_1', 'lawyer_support_2']

function emptyRoles(): RoleEntry[] {
  return ROLE_ORDER.map((p) => ({ position: p, full_name: '', student_id: '' }))
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'Đã hết hạn'
  const totalMin = Math.floor(ms / 60000)
  const days = Math.floor(totalMin / 1440)
  const hours = Math.floor((totalMin % 1440) / 60)
  const mins = totalMin % 60
  if (days > 0) return `${days} ngày ${hours} giờ`
  if (hours > 0) return `${hours} giờ ${mins} phút`
  return `${mins} phút`
}

export interface StructuredRoleSheetDialogProps {
  open: boolean
  onClose: () => void
  caseId: string
  roundId: string
  /** Danh sách team chọn được. Khi length===1 (vd student) sẽ auto-pick và ẩn picker. */
  teams: Array<{ id: string; name: string }>
  onSubmitted: () => void
  /** Tiêu đề vòng để hiển thị trên title — vd "Bán kết", "Chung kết". */
  roundLabel?: string
}

export default function StructuredRoleSheetDialog({
  open,
  onClose,
  caseId,
  roundId,
  teams,
  onSubmitted,
  roundLabel,
}: StructuredRoleSheetDialogProps) {
  const [teamId, setTeamId] = useState('')
  const [representing, setRepresenting] = useState<'plaintiff' | 'defendant'>('plaintiff')
  const [roles, setRoles] = useState<RoleEntry[]>(emptyRoles())
  const [submitErr, setSubmitErr] = useState<string | null>(null)

  const [submitStructured, { isLoading: submitting }] =
    useSubmitStructuredRoleSheetMutation()

  // Auto-pick team khi chỉ có 1 đội (student case)
  useEffect(() => {
    if (!open) return
    if (!teamId && teams.length === 1) setTeamId(teams[0].id)
  }, [open, teams, teamId])

  // Reset form khi mở dialog
  useEffect(() => {
    if (open) {
      setRoles(emptyRoles())
      setSubmitErr(null)
    }
  }, [open])

  const { data: roundSheet } = useGetTeamRoundSheetQuery(
    { case_id: caseId, team_id: teamId, round_id: roundId },
    { skip: !open || !caseId || !teamId || !roundId },
  )

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!open) return
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [open])

  const deadlineMs = useMemo(() => {
    if (!roundSheet?.deadline_iso) return null
    return new Date(roundSheet.deadline_iso).getTime()
  }, [roundSheet?.deadline_iso])

  const remainingMs = deadlineMs ? deadlineMs - now : null
  const willBeLate = remainingMs !== null && remainingMs <= 0

  const updateRole = (pos: RolePosition, patch: Partial<RoleEntry>) => {
    setRoles((prev) => prev.map((r) => (r.position === pos ? { ...r, ...patch } : r)))
  }

  const handleSubmit = async () => {
    setSubmitErr(null)
    if (!teamId) {
      setSubmitErr('Chọn đội thi.')
      return
    }
    for (const r of roles) {
      if (!r.full_name.trim()) {
        setSubmitErr(`Họ tên cho vai "${ROLE_LABELS[r.position]}" không được trống.`)
        return
      }
    }
    try {
      await submitStructured({
        case_id: caseId,
        round_id: roundId,
        team_id: teamId,
        representing_role: representing,
        roles: roles.map((r) => ({
          ...r,
          full_name: r.full_name.trim(),
          student_id: r.student_id?.trim() || null,
        })),
      }).unwrap()
      onSubmitted()
    } catch (e: any) {
      setSubmitErr(e?.data?.detail || e?.message || 'Nộp thất bại')
    }
  }

  const existing = roundSheet?.sheet
  const canEdit = !existing
  const showTeamPicker = teams.length > 1

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AssignmentInd color="primary" />
          <span>Bảng phân vai — Vòng {roundLabel || 'Bán kết'}</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {showTeamPicker && (
            <FormControl size="small" fullWidth>
              <InputLabel>Đội thi</InputLabel>
              <Select
                value={teamId}
                label="Đội thi"
                onChange={(e) => setTeamId(e.target.value)}
                disabled={!!existing}
              >
                {teams.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {deadlineMs && (
            <Alert
              severity={willBeLate ? 'error' : remainingMs! < 24 * 3600_000 ? 'warning' : 'info'}
              icon={<Schedule />}
            >
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <span>
                  <b>Hạn nộp (T-48h):</b> {new Date(deadlineMs).toLocaleString('vi-VN')}
                </span>
                <Chip
                  size="small"
                  color={willBeLate ? 'error' : 'default'}
                  label={`Còn lại: ${formatRemaining(remainingMs!)}`}
                />
                {willBeLate && (
                  <Chip size="small" color="warning" icon={<Warning />} label="Sẽ bị trừ −20đ" />
                )}
              </Stack>
            </Alert>
          )}

          {existing && (
            <Alert severity={existing.is_late ? 'warning' : 'success'}>
              Đội đã nộp Bảng phân vai
              {existing.is_late
                ? ` MUỘN ${existing.minutes_late || 0} phút (−20đ).`
                : ' đúng hạn.'}{' '}
              Liên hệ BTC nếu cần chỉnh sửa.
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Phía đại diện (theo bracket)
            </Typography>
            <RadioGroup
              row
              value={existing?.representing_role || representing}
              onChange={(e) => setRepresenting(e.target.value as 'plaintiff' | 'defendant')}
            >
              <FormControlLabel
                value="plaintiff"
                control={<Radio />}
                label="Nguyên đơn"
                disabled={!canEdit}
              />
              <FormControlLabel
                value="defendant"
                control={<Radio />}
                label="Bị đơn"
                disabled={!canEdit}
              />
            </RadioGroup>
          </Box>

          <Divider />

          <Typography variant="subtitle2">4 vai trong cặp đấu</Typography>
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {(existing?.roles || roles).map((r) => (
              <Paper key={r.position} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="body2" color="primary" fontWeight={800} gutterBottom>
                  {ROLE_LABELS[r.position]}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 180px' },
                  }}
                >
                  <TextField
                    label="Họ và tên"
                    value={r.full_name}
                    onChange={(e) =>
                      updateRole(r.position as RolePosition, { full_name: e.target.value })
                    }
                    size="small"
                    fullWidth
                    required
                    disabled={!canEdit}
                  />
                  <TextField
                    label="MSSV"
                    value={r.student_id || ''}
                    onChange={(e) =>
                      updateRole(r.position as RolePosition, { student_id: e.target.value })
                    }
                    size="small"
                    disabled={!canEdit}
                  />
                </Box>
              </Paper>
            ))}
          </Box>

          {submitErr && <Alert severity="error">{submitErr}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Đóng</Button>
        {canEdit && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !teamId}
            startIcon={<CheckCircle />}
          >
            {submitting ? 'Đang nộp…' : 'Nộp bảng phân vai'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
